import { Fragment, useContext } from 'react';
import { Card } from '@/components/ui/Card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { Views, type ViewValues } from '@/constants';
import { ConfigContext } from '@/context';
import { ArrowLeft } from '@phosphor-icons/react';
import { ConsentCategoryItem } from '@/types';

type Props = {
  setSelectedView: (view: ViewValues) => void;
  consentCategory: ConsentCategoryItem | null;
};

export function ConsentCategoryDetail({
  setSelectedView,
  consentCategory,
}: Props) {
  const {
    config: { trackers = [] } = {},
    consentCategories,
    consentSettingsUnidentifiedCompany,
    noItems,
  } = useContext(ConfigContext);

  if (!consentCategory) {
    return null;
  }

  const { subcategory } = consentCategory;
  const consentTrackers = trackers.filter(
    (val) => val.category === subcategory,
  );

  const groupedConsentTrackers = consentTrackers.reduce<
    Record<string, typeof consentTrackers>
  >((acc, item) => {
    const key = item.company || consentSettingsUnidentifiedCompany;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const title = (
    <>
      <div className="flex items-center">
        <ArrowLeft
          className="mr-4 cursor-pointer"
          onClick={() => setSelectedView(Views.ConsentManagement)}
        />
        {consentCategories[subcategory].title}
      </div>
    </>
  );

  return (
    <div className="flex flex-col space-y-6">
      <Card title={title}>
        <Accordion className="w-full" type="multiple">
          <div className="space-y-4">
            {Object.keys(groupedConsentTrackers).length > 0 ? (
              Object.entries(groupedConsentTrackers).map(([company, items]) => (
                <AccordionItem
                  key={company}
                  className="border-card-border rounded-card border bg-transparent"
                  value={company}
                >
                  <>
                    <AccordionTrigger className="text-subheader hover:text-primary rounded-card min-h-12 hover:shadow-md">
                      <span className="flex-1 text-left text-sm font-semibold">
                        {company}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="p-4">
                      <div className="flex flex-col">
                        {items.map((item, index) => {
                          const keys = Object.keys(
                            Object.fromEntries(
                              Object.entries({
                                name: item.name,
                                pattern: item.pattern,
                                domain: item.domain,
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                              }).filter(([_, v]) => v !== undefined),
                            ),
                          );
                          return (
                            <Fragment key={index}>
                              {keys.map((key) => (
                                <div
                                  key={key}
                                  className="text-muted-foreground flex gap-2"
                                >
                                  <span className="font-bold">
                                    {consentCategories.details[key]}:
                                  </span>
                                  <span>
                                    {/* @ts-ignore */}
                                    {item[key]}
                                  </span>
                                </div>
                              ))}
                              {index !== items.length - 1 && (
                                <hr className="my-4" />
                              )}
                            </Fragment>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </>
                </AccordionItem>
              ))
            ) : (
              <h3>{noItems}</h3>
            )}
          </div>
        </Accordion>
      </Card>
    </div>
  );
}
